import React, { useState, useEffect, useRef } from 'react';
import * as THREE from 'three';
import { Calendar, Info, ChevronLeft, ChevronRight, MapPin, Clock, Globe, Star } from 'lucide-react';

// Helper functions for astronomical data
const getStarColor = (spectralClass) => {
  const type = spectralClass.charAt(0);
  switch(type) {
    case 'O': case 'B': return '#9bb0ff'; // Blue
    case 'A': return '#aabfff'; // Blue-white
    case 'F': return '#cad7ff'; // White
    case 'G': return '#fff4ea'; // Yellow-white
    case 'K': return '#ff7518'; // Orange
    case 'M': return '#ff4500'; // Red
    default: return '#ffffff'; // White fallback
  }
};

// Parse HYG Database CSV
const parseHYGData = (csvText) => {
  try {
    console.log("Starting to parse HYG data...");
    console.log("First 500 characters:", csvText.substring(0, 500));
    
    const lines = csvText.split('\n');
    console.log(`CSV has ${lines.length} lines`);
    
    if (lines.length < 2) {
      console.error("CSV has too few lines, possibly empty or malformed");
      return [];
    }
    
    // Clean header names by removing quotes
    const headers = lines[0].split(',').map(header => header.replace(/"/g, ''));
    console.log("CSV headers (cleaned):", headers);
    
    // Find indices for the columns we need
    const idIdx = headers.indexOf('id');
    const hipIdx = headers.indexOf('hip');
    const raIdx = headers.indexOf('ra');
    const decIdx = headers.indexOf('dec');
    const magIdx = headers.indexOf('mag');
    const properIdx = headers.indexOf('proper');
    const conIdx = headers.indexOf('con');
    const specIdx = headers.indexOf('spect');
    
    console.log("Column indices:", { idIdx, hipIdx, raIdx, decIdx, magIdx, properIdx, conIdx, specIdx });
    
    // Check if essential columns are missing
    if (raIdx === -1 || decIdx === -1 || magIdx === -1) {
      console.error("Essential columns missing from HYG data");
      return [];
    }
    
    const stars = [];
    let processedWithNames = 0;
    let skippedDim = 0;
    let skippedMissingData = 0;
    let skippedNoName = 0;
    
    // Skip header row, process data rows
    for (let i = 1; i < lines.length; i++) {
      if (!lines[i].trim()) continue;
      
      // Handle quoted values in CSV
      const values = [];
      let inQuote = false;
      let currentValue = '';
      
      for (let j = 0; j < lines[i].length; j++) {
        const char = lines[i][j];
        
        if (char === '"') {
          inQuote = !inQuote;
        } else if (char === ',' && !inQuote) {
          values.push(currentValue.replace(/"/g, ''));
          currentValue = '';
        } else {
          currentValue += char;
        }
      }
      
      // Don't forget the last value
      values.push(currentValue.replace(/"/g, ''));
      
      // For debugging - log a few rows
      if (i < 5) {
        console.log(`Row ${i}:`, values);
      }
      
      // We'll try to be more lenient - not requiring proper names
      const hasProperName = properIdx !== -1 && values[properIdx] && values[properIdx].trim() !== '';
      
      // Skip stars without necessary data
      if (values.length <= Math.max(raIdx, decIdx, magIdx)) {
        skippedMissingData++;
        continue;
      }
      
      const hip = hipIdx !== -1 && values[hipIdx] ? parseInt(values[hipIdx]) : null;
      const ra = parseFloat(values[raIdx]) * 15; // Convert hours to degrees
      const dec = parseFloat(values[decIdx]);
      const mag = parseFloat(values[magIdx]);
      
      // Skip very dim stars
      if (mag > 5.5) {
        skippedDim++;
        continue;
      }
      
      // Only include stars with valid data
      if (isNaN(ra) || isNaN(dec) || isNaN(mag)) {
        skippedMissingData++;
        continue;
      }
      
      const spectralClass = specIdx !== -1 && values[specIdx] ? values[specIdx] : 'F0';
      const color = getStarColor(spectralClass);
      
      // Use proper name if available, otherwise use HIP or ID as a name
      const name = hasProperName ? values[properIdx] : 
                  (hip ? `HIP ${hip}` : `Star ${values[idIdx] || i}`);
      
      if (!hasProperName) {
        skippedNoName++;
      } else {
        processedWithNames++;
      }
      
      stars.push({
        id: idIdx !== -1 ? parseInt(values[idIdx]) : i,
        hip: hip,
        name: name,
        ra: ra,
        dec: dec,
        mag: mag,
        color: color,
        constellation: conIdx !== -1 ? (values[conIdx] || 'Unknown') : 'Unknown',
      });
    }
    
    console.log(`Processed ${stars.length} stars total`);
    console.log(`Stars with proper names: ${processedWithNames}`);
    console.log(`Skipped due to: dim stars: ${skippedDim}, missing data: ${skippedMissingData}, no name: ${skippedNoName}`);
    
    // Sort by brightness (magnitude)
    return stars.sort((a, b) => a.mag - b.mag);
  } catch (error) {
    console.error("Error parsing HYG data:", error);
    return [];
  }
};

// Add a new function to parse constellation CSV data
const parseConstellationCSV = (csvText) => {
  try {
    console.log("Starting to parse constellation data...");
    console.log("First 500 characters:", csvText.substring(0, 500));
    
    const lines = csvText.split('\n');
    console.log(`Constellation CSV has ${lines.length} lines`);
    
    if (lines.length < 2) {
      console.error("Constellation CSV has too few lines, possibly empty or malformed");
      return [];
    }
    
    // Clean header names by removing quotes
    const headers = lines[0].split(',').map(header => header.replace(/"/g, ''));
    console.log("Constellation CSV headers (cleaned):", headers);
    
    // Find indices for the columns we need - try different potential column names
    const nameIdx = headers.indexOf('constellation_name') !== -1 ? 
                    headers.indexOf('constellation_name') : 
                    headers.indexOf('name');
                    
    const abbrevIdx = headers.indexOf('abbreviation') !== -1 ? 
                     headers.indexOf('abbreviation') : 
                     headers.indexOf('abbr');
    
    // Try different naming conventions for the line segment columns
    let line1Idx = headers.indexOf('line1');
    let line2Idx = headers.indexOf('line2');
    
    // If not found, try alternative column names
    if (line1Idx === -1 || line2Idx === -1) {
      line1Idx = headers.indexOf('star1');
      line2Idx = headers.indexOf('star2');
    }
    
    // If still not found, look for any columns with 'hip' in the name
    if (line1Idx === -1 || line2Idx === -1) {
      for (let i = 0; i < headers.length; i++) {
        if (headers[i].toLowerCase().includes('hip') || headers[i].toLowerCase().includes('id')) {
          if (line1Idx === -1) {
            line1Idx = i;
          } else if (line2Idx === -1) {
            line2Idx = i;
            break;
          }
        }
      }
    }
    
    console.log("Constellation column indices:", { nameIdx, abbrevIdx, line1Idx, line2Idx });
    
    // Check if essential columns are missing
    if (line1Idx === -1 || line2Idx === -1) {
      console.error("Essential columns missing from constellation data");
      return [];
    }
    
    const constellationLines = [];
    
    // Skip header row, process data rows
    for (let i = 1; i < lines.length; i++) {
      if (!lines[i].trim()) continue;
      
      // Handle quoted values in CSV
      const values = [];
      let inQuote = false;
      let currentValue = '';
      
      for (let j = 0; j < lines[i].length; j++) {
        const char = lines[i][j];
        
        if (char === '"') {
          inQuote = !inQuote;
        } else if (char === ',' && !inQuote) {
          values.push(currentValue.replace(/"/g, ''));
          currentValue = '';
        } else {
          currentValue += char;
        }
      }
      
      // Don't forget the last value
      values.push(currentValue.replace(/"/g, ''));
      
      // For debugging - log a few rows
      if (i < 5) {
        console.log(`Constellation Row ${i}:`, values);
      }
      
      // Skip rows with insufficient data
      if (values.length <= Math.max(line1Idx, line2Idx)) {
        continue;
      }
      
      const name = nameIdx !== -1 ? values[nameIdx] || 'Unknown' : 'Unknown';
      const abbrev = abbrevIdx !== -1 ? values[abbrevIdx] || name.substring(0, 3).toUpperCase() : name.substring(0, 3).toUpperCase();
      
      // Process line segments if they exist
      if (values[line1Idx] && values[line2Idx]) {
        // Try to handle different formats
        let star1, star2;
        
        try {
          // Try parsing as integers first
          star1 = parseInt(values[line1Idx]);
          star2 = parseInt(values[line2Idx]);
          
          // If NaN, try removing quotes and other characters
          if (isNaN(star1)) {
            star1 = parseInt(values[line1Idx].replace(/['"]/g, ''));
          }
          if (isNaN(star2)) {
            star2 = parseInt(values[line2Idx].replace(/['"]/g, ''));
          }
        } catch (error) {
          console.warn(`Error parsing star IDs in row ${i}:`, error);
          continue;
        }
        
        if (!isNaN(star1) && !isNaN(star2)) {
          constellationLines.push({
            from: star1,
            to: star2,
            constellation: abbrev
          });
        }
      }
    }
    
    console.log(`Processed ${constellationLines.length} constellation lines`);
    
    return constellationLines;
  } catch (error) {
    console.error("Error parsing constellation data:", error);
    return [];
  }
};

const getDefaultStars = () => {
  console.log('Creating default star data');
  return [
    { id: 1, name: "Sirius", ra: 101.287, dec: -16.716, mag: -1.46, color: "#9bb0ff", constellation: "Canis Major", hip: 32349 },
    { id: 2, name: "Canopus", ra: 95.988, dec: -52.696, mag: -0.74, color: "#fff4ea", constellation: "Carina", hip: 30438 },
    { id: 3, name: "Arcturus", ra: 213.915, dec: 19.182, mag: -0.05, color: "#ff7518", constellation: "Boötes", hip: 69673 },
    { id: 4, name: "Vega", ra: 279.234, dec: 38.784, mag: 0.03, color: "#9bb0ff", constellation: "Lyra", hip: 91262 },
    { id: 5, name: "Capella", ra: 79.172, dec: 45.998, mag: 0.08, color: "#fff4ea", constellation: "Auriga", hip: 24608 },
    { id: 6, name: "Rigel", ra: 78.634, dec: -8.202, mag: 0.13, color: "#9bb0ff", constellation: "Orion", hip: 24436 },
    { id: 7, name: "Betelgeuse", ra: 88.793, dec: 7.407, mag: 0.50, color: "#ff4500", constellation: "Orion", hip: 27989 },
    { id: 8, name: "Altair", ra: 297.696, dec: 8.868, mag: 0.77, color: "#fff4ea", constellation: "Aquila", hip: 97649 },
    { id: 9, name: "Aldebaran", ra: 68.980, dec: 16.509, mag: 0.85, color: "#ff7518", constellation: "Taurus", hip: 21421 },
    { id: 10, name: "Antares", ra: 247.352, dec: -26.432, mag: 0.96, color: "#ff4500", constellation: "Scorpius", hip: 80763 },
    { id: 11, name: "Spica", ra: 201.298, dec: -11.161, mag: 0.97, color: "#9bb0ff", constellation: "Virgo", hip: 65474 },
    { id: 12, name: "Pollux", ra: 116.329, dec: 28.026, mag: 1.14, color: "#ff7518", constellation: "Gemini", hip: 37826 },
    { id: 13, name: "Deneb", ra: 310.358, dec: 45.280, mag: 1.25, color: "#fff4ea", constellation: "Cygnus", hip: 102098 },
    { id: 14, name: "Regulus", ra: 152.093, dec: 11.967, mag: 1.35, color: "#9bb0ff", constellation: "Leo", hip: 49669 },
    { id: 15, name: "Castor", ra: 113.650, dec: 31.888, mag: 1.57, color: "#fff4ea", constellation: "Gemini", hip: 36850 },
    { id: 16, name: "Bellatrix", ra: 81.283, dec: 6.350, mag: 1.64, color: "#9bb0ff", constellation: "Orion", hip: 25336 },
    { id: 17, name: "Alnilam", ra: 84.053, dec: -1.202, mag: 1.70, color: "#9bb0ff", constellation: "Orion", hip: 26311 },
    { id: 18, name: "Alnitak", ra: 85.190, dec: -1.943, mag: 1.79, color: "#9bb0ff", constellation: "Orion", hip: 26727 },
    { id: 19, name: "Dubhe", ra: 165.932, dec: 61.751, mag: 1.79, color: "#ff7518", constellation: "Ursa Major", hip: 54061 },
    { id: 20, name: "Merak", ra: 165.460, dec: 56.382, mag: 2.37, color: "#fff4ea", constellation: "Ursa Major", hip: 53910 },
    { id: 21, name: "Alioth", ra: 193.507, dec: 55.960, mag: 1.77, color: "#fff4ea", constellation: "Ursa Major", hip: 62956 },
    { id: 22, name: "Alkaid", ra: 206.885, dec: 49.313, mag: 1.86, color: "#9bb0ff", constellation: "Ursa Major", hip: 67301 },
    { id: 23, name: "Polaris", ra: 37.946, dec: 89.264, mag: 1.98, color: "#fff4ea", constellation: "Ursa Minor", hip: 11767 },
    { id: 24, name: "Mintaka", ra: 83.002, dec: -0.299, mag: 2.23, color: "#9bb0ff", constellation: "Orion", hip: 25930 },
    { id: 25, name: "Saiph", ra: 86.939, dec: -9.670, mag: 2.07, color: "#9bb0ff", constellation: "Orion", hip: 26220 }
  ];
};

const getDefaultConstellationLines = () => [
  // Orion - The Hunter
  { from: 27989, to: 25336 }, // Betelgeuse to Bellatrix
  { from: 27989, to: 25930 }, // Betelgeuse to Mintaka
  { from: 25930, to: 26311 }, // Mintaka to Alnilam
  { from: 26311, to: 26727 }, // Alnilam to Alnitak
  { from: 26727, to: 24436 }, // Alnitak to Rigel
  { from: 25336, to: 24436 }, // Bellatrix to Rigel
  { from: 27989, to: 26220 }, // Betelgeuse to Saiph
  { from: 24436, to: 26220 }, // Rigel to Saiph
  
  // Ursa Major - Big Dipper (complete)
  { from: 54061, to: 53910 }, // Dubhe to Merak
  { from: 53910, to: 58001 }, // Merak to Phecda
  { from: 58001, to: 59774 }, // Phecda to Megrez
  { from: 59774, to: 62956 }, // Megrez to Alioth
  { from: 62956, to: 65378 }, // Alioth to Mizar
  { from: 65378, to: 67301 }, // Mizar to Alkaid
  
  // Gemini - The Twins
  { from: 36850, to: 37826 }, // Castor to Pollux
  { from: 36850, to: 35550 }, // Castor to Tejat
  { from: 35550, to: 34088 }, // Tejat to Alhena
  { from: 37826, to: 36962 }, // Pollux to Wasat
  { from: 36962, to: 34088 }, // Wasat to Alhena
  
  // Ursa Minor - Little Dipper
  { from: 11767, to: 85822 }, // Polaris to Yildun
  { from: 85822, to: 82080 }, // Yildun to Epsilon UMi
  { from: 82080, to: 77055 }, // Epsilon UMi to Zeta UMi
  { from: 77055, to: 74793 }, // Zeta UMi to Eta UMi
  { from: 74793, to: 72607 }, // Eta UMi to Kochab
  { from: 72607, to: 75097 }, // Kochab to Pherkad
  
  // Cassiopeia - The "W" shape
  { from: 746, to: 3179 },    // Caph to Schedar
  { from: 3179, to: 4427 },   // Schedar to Gamma Cas
  { from: 4427, to: 6686 },   // Gamma Cas to Ruchbah
  { from: 6686, to: 8886 },   // Ruchbah to Segin
  
  // Lyra - The Harp
  { from: 91262, to: 93194 }, // Vega to Sheliak
  { from: 93194, to: 93773 }, // Sheliak to Sulafat
  { from: 91262, to: 94481 }, // Vega to Delta Lyrae
  
  // Cygnus - The Swan (Northern Cross)
  { from: 102098, to: 100453 }, // Deneb to Sadr
  { from: 100453, to: 97165 },  // Sadr to Albireo
  { from: 100453, to: 95853 },  // Sadr to Delta Cygni
  { from: 100453, to: 102488 }, // Sadr to Gienah Cygni
  
  // Leo - The Lion
  { from: 49669, to: 50583 },  // Regulus to Algieba
  { from: 50583, to: 54879 },  // Algieba to Zosma
  { from: 54879, to: 57632 },  // Zosma to Denebola
  { from: 49669, to: 47908 },  // Regulus to Eta Leonis
  { from: 47908, to: 46146 },  // Eta Leonis to Adhafera
  { from: 46146, to: 50583 },  // Adhafera to Algieba
  
  // Summer Triangle (asterism)
  { from: 91262, to: 102098 }, // Vega to Deneb
  { from: 102098, to: 97649 }, // Deneb to Altair
  { from: 97649, to: 91262 }   // Altair to Vega
];

const NASASkyExplorer = () => {
  const [apodData, setApodData] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState({ status: 'Initializing...', percent: 0 });
  const [selectedObject, setSelectedObject] = useState(null);
  const [userLocation, setUserLocation] = useState(null);
  const [starCatalog, setStarCatalog] = useState([]);
  const [constellationLines, setConstellationLines] = useState([]);
  const [dataSource, setDataSource] = useState({ stars: 'Default', constellations: 'Default' });
  const [usingFallbackData, setUsingFallbackData] = useState(false);
  const mountRef = useRef(null);
  const sceneRef = useRef(null);
  const rendererRef = useRef(null);
  const cameraRef = useRef(null);

  // Convert celestial coordinates to 3D position
  const celestialTo3D = (ra, dec, distance = 300, lat = 40, lng = -74, time = new Date()) => {
    // Convert RA/Dec to radians
    const raRad = (ra * Math.PI) / 180;
    const decRad = (dec * Math.PI) / 180;
    
    // Local Sidereal Time calculation (simplified)
    const jd = (time.getTime() / 86400000) + 2440587.5;
    const lst = ((280.46061837 + 360.98564736629 * (jd - 2451545)) % 360 + lng) * Math.PI / 180;
    
    // Hour angle
    const ha = lst - raRad;
    
    // Convert to local horizontal coordinates
    const latRad = (lat * Math.PI) / 180;
    const sinAlt = Math.sin(decRad) * Math.sin(latRad) + 
                   Math.cos(decRad) * Math.cos(latRad) * Math.cos(ha);
    const alt = Math.asin(sinAlt);
    
    const cosAz = (Math.sin(decRad) - Math.sin(alt) * Math.sin(latRad)) / 
                  (Math.cos(alt) * Math.cos(latRad));
    let az = Math.acos(Math.max(-1, Math.min(1, cosAz)));
    
    if (Math.sin(ha) > 0) az = 2 * Math.PI - az;
    
    // Convert to 3D Cartesian coordinates
    const x = distance * Math.cos(alt) * Math.sin(az);
    const y = distance * Math.sin(alt);
    const z = -distance * Math.cos(alt) * Math.cos(az);
    
    return { x, y, z, altitude: alt * 180 / Math.PI };
  };

  // Get user location from IP
  useEffect(() => {
    const getUserLocation = async () => {
      try {
        const response = await fetch('https://ipapi.co/json/');
        const data = await response.json();
        console.log('User location data:', data);
        setUserLocation({
          latitude: data.latitude || 40.7128,
          longitude: data.longitude || -74.0060,
          city: data.city || 'New York',
          country: data.country_name || 'United States'
        });
      } catch (error) {
        console.error('Error getting location:', error);
        // Default to New York
        setUserLocation({
          latitude: 40.7128,
          longitude: -74.0060,
          city: 'New York',
          country: 'United States'
        });
      }
    };

    getUserLocation();
  }, []);

  // Load astronomical data
  useEffect(() => {
    const loadAstronomicalData = async () => {
      try {
        console.log('Loading astronomical data...');
        setLoadingProgress({ status: 'Connecting to astronomical databases...', percent: 10 });
        
        // Try multiple possible URLs for the HYG database
        const hygUrls = [
          './data/hygdata_v41.csv',
          'https://raw.githubusercontent.com/astronexus/HYG-Database/refs/heads/main/hyg/CURRENT/hygdata_v41.csv',
          'https://raw.githubusercontent.com/astronexus/HYG-Database/main/hygdata_v41.csv',  
          'https://raw.githubusercontent.com/astronexus/HYG-Database/master/hygdata_v41.csv',
          'https://raw.githubusercontent.com/astronexus/HYG-Database/master/hygdata_v3.csv',
          'https://raw.githubusercontent.com/astronexus/HYG-Database/master/hygfull.csv'
        ];

        let stars = [];
        let starDataLoaded = false;

        // Force using default data for debugging
        const forceDefaultData = false; // Set to true to bypass API and use default data
        
        if (forceDefaultData) {
          console.log('Forcing default star data for debugging');
          stars = getDefaultStars();
          setDataSource(prev => ({ ...prev, stars: 'Default (forced)' }));
          starDataLoaded = true;
        } else {
          // Try each URL until one works
          for (const url of hygUrls) {
            if (starDataLoaded) break;
            
            try {
              console.log(`Trying HYG Database URL: ${url}`);
              const starResponse = await fetch(url);
              
              if (starResponse.ok) {
                setLoadingProgress({ status: 'Downloading star data...', percent: 40 });
                const csvText = await starResponse.text();
                
                // Check if we got valid data
                if (csvText.trim().length > 0 && csvText.includes(',')) {
                  setLoadingProgress({ status: 'Processing star data...', percent: 60 });
                  console.log('Parsing HYG Database...');
                  stars = parseHYGData(csvText);
                  console.log(`Loaded ${stars.length} stars from HYG Database`);
                  
                  if (stars.length > 0) {
                    setDataSource(prev => ({ ...prev, stars: 'HYG Database v4.1' }));
                    starDataLoaded = true;
                    break;
                  } else {
                    console.warn(`No stars loaded from ${url}`);
                  }
                } else {
                  console.warn(`Invalid data format from ${url}`);
                }
              } else {
                console.warn(`Failed to fetch HYG Database from ${url}`);
              }
            } catch (error) {
              console.error(`Error fetching HYG Database from ${url}:`, error);
            }
          }
        }

        // If all URLs failed, use default data
        if (!starDataLoaded) {
          console.warn('All HYG Database URLs failed, using fallback data');
          setLoadingProgress({ status: 'Using default star data...', percent: 50 });
          stars = getDefaultStars();
          setDataSource(prev => ({ ...prev, stars: 'Default' }));
          setUsingFallbackData(true);
        }
        
        // Print detailed info about the star data
        console.log('Star data summary:', {
          count: stars.length,
          hasValidPositions: stars.every(s => typeof s.ra === 'number' && typeof s.dec === 'number'),
          brightest: stars.length > 0 ? stars.slice(0, 3).map(s => `${s.name} (mag ${s.mag})`) : 'None',
          constellations: [...new Set(stars.map(s => s.constellation))].join(', ')
        });

        // Load constellation line data from CSV format
        setLoadingProgress({ status: 'Fetching constellation data...', percent: 70 });
        console.log('Fetching constellation data...');
        
        // Force using default constellation data for debugging
        const forceDefaultConstellations = true;
        
        let lines = [];
        
        if (forceDefaultConstellations) {
          console.log('Using default constellation data');
          lines = getDefaultConstellationLines();
          setDataSource(prev => ({ ...prev, constellations: 'Default (forced)' }));
        } else {
          // Function to inspect data formats
          const inspectDataFormat = async (url) => {
            try {
              console.log(`Inspecting data format for: ${url}`);
              const response = await fetch(url);
              if (response.ok) {
                const text = await response.text();
                // Log first few lines to inspect format
                const firstLines = text.split('\n').slice(0, 5).join('\n');
                console.log(`Sample data from ${url}:\n${firstLines}`);
                return { success: true, text };
              } else {
                console.warn(`Failed to fetch ${url}`);
                return { success: false };
              }
            } catch (error) {
              console.error(`Error inspecting data format for ${url}:`, error);
              return { success: false };
            }
          };

          // Try each file and use the first one that works
          let constellationData = null;
          for (const file of dataFiles) {
            const url = `./data/${file}`;
            const result = await inspectDataFormat(url);
            if (result.success) {
              console.log(`Successfully loaded ${file}`);
              constellationData = { file, text: result.text };
              break;
            }
          }
          
          // Additional constellation data sources to try if the first ones fail
          if (!constellationData) {
            const additionalSources = [
              'https://raw.githubusercontent.com/astronexus/HYG-Database/master/constellations.dat',
              'https://raw.githubusercontent.com/ofrohn/d3-celestial/master/data/constellations.lines.json',
              'https://raw.githubusercontent.com/skylergrammer/Hipparcos-Catalog-Viz/master/data/constellation_lines.csv'
            ];
            
            for (const url of additionalSources) {
              console.log(`Trying alternative constellation source: ${url}`);
              const result = await inspectDataFormat(url);
              if (result.success) {
                console.log(`Successfully loaded alternative constellation data from ${url}`);
                constellationData = { file: url.split('/').pop(), text: result.text };
                break;
              }
            }
          }
          
          // Define lines variable
          lines = [];
          
          // Parse the data based on the file format
          if (constellationData) {
            setLoadingProgress({ status: `Processing ${constellationData.file}...`, percent: 80 });
            try {
              // Check if it's JSON format
              if (constellationData.file.endsWith('.json')) {
                try {
                  const jsonData = JSON.parse(constellationData.text);
                  console.log('Parsed JSON constellation data:', jsonData);
                  
                  // Try to extract constellation lines based on common JSON formats
                  if (Array.isArray(jsonData)) {
                    lines = jsonData.flatMap(constellation => {
                      // Check if this JSON has a 'lines' property (common format)
                      if (constellation.lines && Array.isArray(constellation.lines)) {
                        return constellation.lines.map(line => ({
                          from: line[0],
                          to: line[1],
                          constellation: constellation.id || constellation.abbr || 'UNK'
                        }));
                      }
                      return [];
                    });
                  }
                } catch (jsonError) {
                  console.error('Error parsing JSON constellation data:', jsonError);
                }
              } 
              // If not JSON or JSON parsing failed, try CSV
              else {
                lines = parseConstellationCSV(constellationData.text);
              }
              
              console.log(`Loaded ${lines.length} constellation lines from ${constellationData.file}`);
              setDataSource(prev => ({ ...prev, constellations: `Western Constellations (${constellationData.file})` }));
            } catch (error) {
              console.error(`Error parsing ${constellationData.file}:`, error);
              // Fall back to default constellation lines
              lines = getDefaultConstellationLines();
              setDataSource(prev => ({ ...prev, constellations: 'Default' }));
            }
          } else {
            // Fall back to default constellation lines if all attempts fail
            console.warn('Failed to fetch any constellation data, using fallback data');
            setLoadingProgress({ status: 'Using default constellation data...', percent: 80 });
            lines = getDefaultConstellationLines();
            setDataSource(prev => ({ ...prev, constellations: 'Default' }));
          }
        }
        
        // If we got very few constellation lines, try to generate some based on proximity
        if (lines.length < 10) {
          console.log('Few constellation lines found, generating proximity-based connections');
          
          // Group stars by constellation
          const constellationGroups = {};
          starCatalog.forEach(star => {
            if (star.constellation && star.constellation !== 'Unknown') {
              if (!constellationGroups[star.constellation]) {
                constellationGroups[star.constellation] = [];
              }
              constellationGroups[star.constellation].push(star);
            }
          });
          
          // For each constellation with multiple stars, connect them by proximity
          Object.entries(constellationGroups).forEach(([constellation, stars]) => {
            if (stars.length > 1) {
              // Sort by brightness (mag)
              const sortedStars = [...stars].sort((a, b) => a.mag - b.mag);
              
              // Connect each star to the next brightest star
              for (let i = 0; i < sortedStars.length - 1; i++) {
                lines.push({
                  from: sortedStars[i].hip || sortedStars[i].id,
                  to: sortedStars[i + 1].hip || sortedStars[i + 1].id,
                  constellation: constellation
                });
              }
            }
          });
          
          console.log(`Generated ${lines.length} proximity-based constellation lines`);
          setDataSource(prev => ({ ...prev, constellations: 'Generated (brightness-based)' }));
        }
        
        setLoadingProgress({ status: 'Building 3D star map...', percent: 90 });
        console.log(`Finished loading astronomical data: ${stars.length} stars and ${lines.length} constellation lines`);
        
        // Set the star catalog state - this is crucial for rendering
        console.log('About to set star catalog state. Stars to render:', stars.length > 0 ? stars.slice(0, 3) : 'None');
        setStarCatalog(stars.slice(0, 500)); // Limit for performance
        setConstellationLines(lines);
        
      } catch (error) {
        console.error('Error loading astronomical data:', error);
        // Use fallback data
        setLoadingProgress({ status: 'Using backup data...', percent: 50 });
        setStarCatalog(getDefaultStars());
        setConstellationLines(getDefaultConstellationLines());
        setDataSource({ stars: 'Default', constellations: 'Default' });
        setUsingFallbackData(true);
      }
    };

    loadAstronomicalData();
  }, []);

  // Initialize Three.js scene with real stars
  useEffect(() => {
    console.log('Three.js initialization. Conditions:', {
      mountRefExists: !!mountRef.current,
      userLocation,
      starCatalogLength: starCatalog.length,
      constellationLinesLength: constellationLines.length
    });
    
    if (!mountRef.current || !userLocation || starCatalog.length === 0) {
      console.warn('Cannot initialize Three.js scene - prerequisites not met', {
        mountRef: !!mountRef.current,
        userLocation: !!userLocation,
        starCatalog: starCatalog.length
      });
      return;
    }

    console.log('Starting Three.js scene setup');

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 1000);
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setClearColor(0x000011, 1);
    mountRef.current.appendChild(renderer.domElement);

    // Create background stars (dimmer, procedural)
    const backgroundStarGeometry = new THREE.BufferGeometry();
    const backgroundStarCount = 5000;
    const backgroundStarPositions = new Float32Array(backgroundStarCount * 3);
    const backgroundStarColors = new Float32Array(backgroundStarCount * 3);

    for (let i = 0; i < backgroundStarCount; i++) {
      const radius = 500;
      const theta = Math.random() * 2 * Math.PI;
      const phi = Math.acos(2 * Math.random() - 1);
      
      backgroundStarPositions[i * 3] = radius * Math.sin(phi) * Math.cos(theta);
      backgroundStarPositions[i * 3 + 1] = radius * Math.sin(phi) * Math.sin(theta);
      backgroundStarPositions[i * 3 + 2] = radius * Math.cos(phi);

      const brightness = 0.2 + Math.random() * 0.3;
      backgroundStarColors[i * 3] = brightness;
      backgroundStarColors[i * 3 + 1] = brightness;
      backgroundStarColors[i * 3 + 2] = brightness;
    }

    backgroundStarGeometry.setAttribute('position', new THREE.BufferAttribute(backgroundStarPositions, 3));
    backgroundStarGeometry.setAttribute('color', new THREE.BufferAttribute(backgroundStarColors, 3));

    const backgroundStarMaterial = new THREE.PointsMaterial({
      size: 1,
      sizeAttenuation: true,
      vertexColors: true,
      transparent: true,
      opacity: 0.6
    });

    const backgroundStars = new THREE.Points(backgroundStarGeometry, backgroundStarMaterial);
    scene.add(backgroundStars);

    // Create real bright stars
    const realStarsGroup = new THREE.Group();
    const currentTime = new Date();
    
    console.log('About to create stars. StarCatalog:', {
      length: starCatalog.length,
      sample: starCatalog.slice(0, 3)
    });

    let visibleStarCount = 0;
    starCatalog.forEach(star => {
      const pos = celestialTo3D(
        star.ra, 
        star.dec, 
        300, 
        userLocation.latitude, 
        userLocation.longitude, 
        currentTime
      );

      // Only show stars above horizon
      if (pos.altitude > -10) {
        visibleStarCount++;
        // Star size based on magnitude (brighter stars are bigger)
        const size = Math.max(2, 8 - star.mag);
        
        const starGeometry = new THREE.SphereGeometry(size * 0.5, 8, 8);
        const starMaterial = new THREE.MeshBasicMaterial({
          color: star.color,
          transparent: true,
          opacity: Math.max(0.6, 1 - star.mag / 5)
        });
        
        const starMesh = new THREE.Mesh(starGeometry, starMaterial);
        starMesh.position.set(pos.x, pos.y, pos.z);
        starMesh.userData = {
          name: star.name,
          magnitude: star.mag,
          constellation: star.constellation,
          hip: star.hip,
          isRealStar: true,
          altitude: pos.altitude
        };
        
        // Add glow for bright stars
        if (star.mag < 1) {
          const glowGeometry = new THREE.SphereGeometry(size * 1.5, 8, 8);
          const glowMaterial = new THREE.MeshBasicMaterial({
            color: star.color,
            transparent: true,
            opacity: 0.2
          });
          const glow = new THREE.Mesh(glowGeometry, glowMaterial);
          starMesh.add(glow);
        }
        
        realStarsGroup.add(starMesh);
      }
    });

    console.log(`Created ${visibleStarCount} visible stars out of ${starCatalog.length} total stars`);

    scene.add(realStarsGroup);

    // Add constellation lines using Hipparcos IDs
    const constellationGroup = new THREE.Group();
    
    // Debug information about constellation connections
    console.log(`Attempting to render ${constellationLines.length} constellation lines`);
    console.log(`First few constellation lines:`, constellationLines.slice(0, 5));
    console.log(`First few stars in catalog:`, starCatalog.slice(0, 5));
    
    let connectedLines = 0;
    let skippedLines = 0;
    
    // Track constellation centers for labels
    const constellationCenters = {};
    
    constellationLines.forEach(line => {
      // Try to find stars by HIP ID or by other IDs
      let starFrom = starCatalog.find(s => s.hip === line.from);
      let starTo = starCatalog.find(s => s.hip === line.to);
      
      // If we can't find by HIP, try finding by ID (some constellations may use different IDs)
      if (!starFrom) starFrom = starCatalog.find(s => s.id === line.from);
      if (!starTo) starTo = starCatalog.find(s => s.id === line.to);
      
      if (starFrom && starTo) {
        connectedLines++;
        const posFrom = celestialTo3D(starFrom.ra, starFrom.dec, 300, userLocation.latitude, userLocation.longitude, currentTime);
        const posTo = celestialTo3D(starTo.ra, starTo.dec, 300, userLocation.latitude, userLocation.longitude, currentTime);
        
        if (posFrom.altitude > -10 && posTo.altitude > -10) {
          const lineGeometry = new THREE.BufferGeometry().setFromPoints([
            new THREE.Vector3(posFrom.x, posFrom.y, posFrom.z),
            new THREE.Vector3(posTo.x, posTo.y, posTo.z)
          ]);
          
          const lineMaterial = new THREE.LineBasicMaterial({
            color: 0x4444aa,
            transparent: true,
            opacity: 0.3
          });
          
          const threeLine = new THREE.Line(lineGeometry, lineMaterial);
          constellationGroup.add(threeLine);
          
          // Track constellation centers for labels
          const constellationName = line.constellation || starFrom.constellation || starTo.constellation;
          if (constellationName && constellationName !== 'Unknown') {
            if (!constellationCenters[constellationName]) {
              constellationCenters[constellationName] = {
                count: 0,
                x: 0,
                y: 0,
                z: 0
              };
            }
            
            // Update the center by averaging the line endpoints
            const center = constellationCenters[constellationName];
            center.x += (posFrom.x + posTo.x) / 2;
            center.y += (posFrom.y + posTo.y) / 2;
            center.z += (posFrom.z + posTo.z) / 2;
            center.count++;
          }
        }
      } else {
        skippedLines++;
      }
    });
    
    // Add constellation name labels
    Object.entries(constellationCenters).forEach(([name, center]) => {
      if (center.count > 0) {
        const x = center.x / center.count;
        const y = center.y / center.count;
        const z = center.z / center.count;
        
        // Create a canvas for the label
        const canvas = document.createElement('canvas');
        canvas.width = 256;
        canvas.height = 128;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = 'rgba(0, 0, 0, 0)';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        
        // Draw text
        ctx.font = 'bold 40px Arial';
        ctx.fillStyle = 'rgba(100, 150, 255, 0.8)';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(name, canvas.width / 2, canvas.height / 2);
        
        // Create texture
        const texture = new THREE.CanvasTexture(canvas);
        texture.needsUpdate = true;
        
        // Create sprite material
        const material = new THREE.SpriteMaterial({
          map: texture,
          transparent: true,
          depthWrite: false
        });
        
        // Create sprite
        const sprite = new THREE.Sprite(material);
        sprite.position.set(x, y, z);
        sprite.scale.set(30, 15, 1);
        
        constellationGroup.add(sprite);
      }
    });
    
    console.log(`Constellation rendering: connected ${connectedLines} lines, skipped ${skippedLines} lines`);
    console.log(`Added labels for ${Object.keys(constellationCenters).length} constellations`);

    scene.add(constellationGroup);

    // Add Milky Way background
    const milkyWayGeometry = new THREE.SphereGeometry(400, 32, 32);
    const milkyWayMaterial = new THREE.MeshBasicMaterial({
      color: 0x1a1a2e,
      transparent: true,
      opacity: 0.15,
      side: THREE.BackSide
    });
    const milkyWay = new THREE.Mesh(milkyWayGeometry, milkyWayMaterial);
    scene.add(milkyWay);

    camera.position.set(0, 0, 0);

    // Mouse controls
    let mouseX = 0, mouseY = 0;
    let targetRotationX = 0, targetRotationY = 0;
    let targetFOV = 75;

    const onMouseMove = (event) => {
      mouseX = (event.clientX / window.innerWidth) * 2 - 1;
      mouseY = -(event.clientY / window.innerHeight) * 2 + 1;
      targetRotationX = mouseY * 1.2;
      targetRotationY = -mouseX * 1.2; // Inverted for natural panning
    };

    // Mouse wheel zoom
    const onMouseWheel = (event) => {
      event.preventDefault();
      const delta = event.deltaY > 0 ? 1 : -1;
      targetFOV = Math.max(20, Math.min(120, targetFOV + delta * 5));
    };

    // Click detection for stars
    const raycaster = new THREE.Raycaster();
    const mouse = new THREE.Vector2();

    const onMouseClick = (event) => {
      mouse.x = (event.clientX / window.innerWidth) * 2 - 1;
      mouse.y = -(event.clientY / window.innerHeight) * 2 + 1;

      raycaster.setFromCamera(mouse, camera);
      const intersects = raycaster.intersectObjects(realStarsGroup.children);

      if (intersects.length > 0) {
        const clickedStar = intersects[0].object;
        if (clickedStar.userData.isRealStar) {
          setSelectedObject(clickedStar);
        }
      }
    };

    mountRef.current?.addEventListener('mousemove', onMouseMove);
    mountRef.current?.addEventListener('click', onMouseClick);
    mountRef.current?.addEventListener('wheel', onMouseWheel, { passive: false });

    // Animation loop
    const animate = () => {
      requestAnimationFrame(animate);
      
      camera.rotation.x += (targetRotationX - camera.rotation.x) * 0.05;
      camera.rotation.y += (targetRotationY - camera.rotation.y) * 0.05;
      
      // Smooth zoom animation
      camera.fov += (targetFOV - camera.fov) * 0.1;
      camera.updateProjectionMatrix();
      
      backgroundStars.rotation.y += 0.0001;
      milkyWay.rotation.y += 0.00005;
      
      renderer.render(scene, camera);
    };

    animate();

    // Store refs
    sceneRef.current = scene;
    rendererRef.current = renderer;
    cameraRef.current = camera;

    // Handle resize
    const handleResize = () => {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      mountRef.current?.removeEventListener('mousemove', onMouseMove);
      mountRef.current?.removeEventListener('click', onMouseClick);
      mountRef.current?.removeEventListener('wheel', onMouseWheel);
      if (mountRef.current && renderer.domElement) {
        mountRef.current.removeChild(renderer.domElement);
      }
      renderer.dispose();
    };
  }, [userLocation, starCatalog, constellationLines]);

  // Fetch NASA APOD data
  useEffect(() => {
    const fetchAPODData = async () => {
      try {
        setLoadingProgress({ status: 'Fetching NASA imagery...', percent: 95 });
        const endDate = new Date();
        const startDate = new Date();
        startDate.setDate(startDate.getDate() - 30);

        const formatDate = (date) => date.toISOString().split('T')[0];
        
        console.log('Attempting to fetch APOD data from NASA API...');
        const response = await fetch(
          `https://api.nasa.gov/planetary/apod?api_key=4BXfxa3egWopXlkbE5rIua9im9hHMhXjcOr3e95w&start_date=${formatDate(startDate)}&end_date=${formatDate(endDate)}`
        );
        
        if (!response.ok) {
          console.warn(`NASA API returned status ${response.status}. Using local fallback data.`);
          throw new Error('Failed to fetch APOD data from NASA API');
        }
        
        const data = await response.json();
        console.log(`Successfully loaded ${data.length} APOD entries from NASA API`);
        setApodData(data.reverse());
        setUsingFallbackData(false);
        
      } catch (error) {
        console.error('Error fetching APOD data from NASA API:', error);
        console.log('Falling back to local APOD data...');
        
        try {
          // Try to load the fallback data
          const fallbackResponse = await fetch('./data/nasa_pod.json');
          if (fallbackResponse.ok) {
            const fallbackData = await fallbackResponse.json();
            console.log(`Successfully loaded ${fallbackData.length} APOD entries from local fallback`);
            setApodData(fallbackData);
            setUsingFallbackData(true);
          } else {
            throw new Error('Failed to load fallback data');
          }
        } catch (fallbackError) {
          console.error('Error loading fallback APOD data:', fallbackError);
          // If even the fallback fails, use a minimal dataset
          setApodData([{
            date: new Date().toISOString().split('T')[0],
            title: "Demo: Andromeda Galaxy",
            explanation: "The Andromeda Galaxy is the nearest major galaxy to our Milky Way and is approaching us at about 110 km/s. It contains roughly one trillion stars and is about 2.5 million light-years away.",
            url: "https://images.unsplash.com/photo-1419242902214-272b3f66ee7a?w=800",
            media_type: "image"
          }]);
          setUsingFallbackData(true);
        }
      } finally {
        setLoadingProgress({ status: 'Complete!', percent: 100 });
        setLoading(false);
      }
    };

    // Only fetch APOD data after star catalog is loaded
    if (starCatalog.length > 0) {
      fetchAPODData();
    }
  }, [starCatalog]);

  // Add APOD objects to scene
  useEffect(() => {
    if (!sceneRef.current || !apodData.length || !userLocation) return;

    const objectsToRemove = sceneRef.current.children.filter(child => child.userData.isAPODObject);
    objectsToRemove.forEach(obj => sceneRef.current.remove(obj));

    const currentAPOD = apodData[currentIndex];
    if (currentAPOD) {
      const markerGeometry = new THREE.SphereGeometry(4, 16, 16);
      const markerMaterial = new THREE.MeshBasicMaterial({
        color: 0xff6b6b,
        transparent: true,
        opacity: 0.9
      });
      
      const marker = new THREE.Mesh(markerGeometry, markerMaterial);
      
      // Position based on current time and some astronomical logic
      const angle = (currentIndex / apodData.length) * Math.PI * 2;
      const height = Math.sin(angle) * 100;
      marker.position.set(
        Math.cos(angle) * 250,
        height,
        Math.sin(angle) * 250
      );
      
      marker.userData.isAPODObject = true;
      marker.userData.apodData = currentAPOD;
      
      // Pulsing glow effect
      const glowGeometry = new THREE.SphereGeometry(8, 16, 16);
      const glowMaterial = new THREE.MeshBasicMaterial({
        color: 0xff6b6b,
        transparent: true,
        opacity: 0.4
      });
      const glow = new THREE.Mesh(glowGeometry, glowMaterial);
      marker.add(glow);
      
      sceneRef.current.add(marker);
    }
  }, [currentIndex, apodData, userLocation]);

  const navigateTime = (direction) => {
    if (direction === 'prev' && currentIndex < apodData.length - 1) {
      setCurrentIndex(currentIndex + 1);
    } else if (direction === 'next' && currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const currentAPOD = apodData[currentIndex];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-b from-gray-900 to-black text-white">
        <div className="text-center max-w-md mx-auto">
          <div className="relative mb-6">
            <div className="h-2 bg-gray-800 rounded-full">
              <div 
                className="h-full bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 rounded-full transition-all duration-300 ease-out"
                style={{ width: `${loadingProgress.percent}%` }}
              />
            </div>
            <div className="absolute -top-6 left-0 right-0">
              <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-blue-400 mx-auto"></div>
            </div>
          </div>
          <p className="text-xl mb-2">Loading cosmic data...</p>
          <p className="text-sm text-gray-400">{loadingProgress.status}</p>
          <p className="text-xs text-gray-500 mt-4 italic">
            Using HYG Database & Stellarium constellation data
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen w-screen bg-gradient-to-b from-gray-900 to-black text-white overflow-hidden">
      {/* 3D Sky View - Now takes full screen */}
      <div className="w-full h-full relative">
        <div ref={mountRef} className="w-full h-full cursor-crosshair" />
        
        {/* Location Info Overlay */}
        {userLocation && (
          <div className="absolute top-4 left-4 bg-black/50 backdrop-blur-sm rounded-lg p-3 z-10">
            <div className="flex items-center gap-2 text-sm">
              <Globe size={16} className="text-blue-400" />
              <span>Sky from {userLocation.city}, {userLocation.country}</span>
            </div>
            <p className="text-xs text-gray-300 mt-1">Mouse to pan • Wheel to zoom • Click stars for info</p>
            <div className="text-xs text-gray-400 mt-1 flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-blue-400"></span>
              <span>Data: {dataSource.stars} + {dataSource.constellations}</span>
            </div>
            <div className="text-xs text-gray-400 mt-1">
              <span>Debug: {starCatalog.length} stars, {constellationLines.length} constellation lines</span>
            </div>
          </div>
        )}

        {/* Selected Star Info */}
        {selectedObject && selectedObject.userData.isRealStar && (
          <div className="absolute top-4 right-4 bg-black/70 backdrop-blur-sm rounded-lg p-4 max-w-sm z-10">
            <div className="flex items-center gap-2 mb-2">
              <Star size={16} className="text-yellow-400" />
              <span className="font-bold">{selectedObject.userData.name}</span>
            </div>
            <div className="text-sm text-gray-300 space-y-1">
              <p>Constellation: {selectedObject.userData.constellation}</p>
              <p>Magnitude: {selectedObject.userData.magnitude.toFixed(2)}</p>
              <p>Altitude: {selectedObject.userData.altitude.toFixed(1)}°</p>
            </div>
          </div>
        )}

        {/* Current APOD Object Info */}
        {currentAPOD && (
          <div className="absolute bottom-4 left-4 bg-black/70 backdrop-blur-sm rounded-lg p-4 max-w-sm z-10">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-3 h-3 bg-red-400 rounded-full animate-pulse"></div>
              <span className="text-sm font-medium">NASA Object Located</span>
            </div>
            <h3 className="font-bold text-lg mb-1">{currentAPOD.title}</h3>
            <p className="text-xs text-gray-300">{currentAPOD.date}</p>
          </div>
        )}
      </div>

      {/* Timeline Panel - Now a floating overlay */}
      <div className="absolute top-0 right-0 bottom-0 w-96 bg-gray-900/95 backdrop-blur-sm border-l border-gray-700 flex flex-col z-20">
        {/* Header */}
        <div className="p-6 border-b border-gray-700">
          <div className="flex items-center gap-3 mb-4">
            <Calendar className="text-blue-400" size={24} />
            <h1 className="text-xl font-bold">Cosmic Timeline</h1>
          </div>
          
          {/* Navigation */}
          <div className="flex items-center justify-between bg-gray-800 rounded-lg p-2">
            <button
              onClick={() => navigateTime('prev')}
              disabled={currentIndex >= apodData.length - 1}
              className="p-2 rounded hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft size={20} />
            </button>
            
            <div className="text-center">
              <div className="flex items-center gap-1 text-sm text-gray-300">
                <Clock size={14} />
                <span>Day {currentIndex + 1} of {apodData.length}</span>
              </div>
            </div>
            
            <button
              onClick={() => navigateTime('next')}
              disabled={currentIndex <= 0}
              className="p-2 rounded hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronRight size={20} />
            </button>
          </div>
          
          {/* Fallback data notification */}
          {usingFallbackData && (
            <div className="mt-2 p-2 bg-yellow-900/50 border border-yellow-700/50 rounded-lg text-yellow-300 text-xs">
              <div className="flex items-center gap-1 font-medium">
                <Info size={12} />
                <span>Using offline APOD data</span>
              </div>
              <p className="mt-1 text-yellow-300/70">
                NASA API unavailable. Using locally stored astronomy images.
              </p>
            </div>
          )}
        </div>

        {/* Current APOD Details */}
        {currentAPOD && (
          <div className="flex-1 p-6 overflow-y-auto">
            <div className="space-y-4">
              {/* Image */}
              {currentAPOD.media_type === 'image' && (
                <div className="relative group">
                  <img
                    src={currentAPOD.url}
                    alt={currentAPOD.title}
                    className="w-full h-48 object-cover rounded-lg transition-transform group-hover:scale-105"
                    onError={(e) => {
                      e.target.src = 'https://images.unsplash.com/photo-1419242902214-272b3f66ee7a?w=400';
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent rounded-lg"></div>
                </div>
              )}

              {/* Info */}
              <div>
                <h2 className="text-2xl font-bold mb-2 bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
                  {currentAPOD.title}
                </h2>
                <div className="flex items-center gap-2 text-sm text-gray-400 mb-4">
                  <Calendar size={14} />
                  <span>{new Date(currentAPOD.date).toLocaleDateString('en-US', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}</span>
                </div>
              </div>

              {/* Description */}
              <div className="bg-gradient-to-br from-gray-800/50 to-gray-700/30 rounded-lg p-4 border border-gray-600/30">
                <div className="flex items-center gap-2 mb-3">
                  <Info size={16} className="text-blue-400" />
                  <span className="font-medium">About this cosmic object</span>
                </div>
                <p className="text-sm text-gray-300 leading-relaxed">
                  {currentAPOD.explanation}
                </p>
              </div>

              {/* Copyright */}
              {currentAPOD.copyright && (
                <p className="text-xs text-gray-500 italic">
                  © {currentAPOD.copyright}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Enhanced Timeline */}
        <div className="p-4 border-t border-gray-700 bg-gradient-to-r from-gray-800/50 to-gray-700/30">
          <div className="text-xs text-gray-400 mb-2 flex justify-between">
            <span>Timeline</span>
            <span>{starCatalog.length} stars from {dataSource.stars}</span>
          </div>
          <div className="h-3 bg-gray-800 rounded-full relative overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 rounded-full transition-all duration-500 ease-out"
              style={{ width: `${((apodData.length - currentIndex) / apodData.length) * 100}%` }}
            ></div>
            <div
              className="absolute top-1/2 transform -translate-y-1/2 w-5 h-5 bg-white rounded-full shadow-lg transition-all duration-300 border-2 border-blue-400"
              style={{ left: `${((apodData.length - currentIndex - 1) / Math.max(apodData.length - 1, 1)) * 100}%` }}
            ></div>
          </div>
          <div className="flex justify-between text-xs text-gray-500 mt-2">
            <span>30 days ago</span>
            <span>Today</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NASASkyExplorer;